<template>
    <div v-if="!loading && transactions" class="w-full">
        <div v-if="total && total>limit && count" class="flex flex-1 align-items-center mb-3">
            <span class="text-xs text-400">{{ count }} {{ content[lang_].from }} {{ total }} {{ content[lang_].trans }}</span>
        </div>
        <div v-if="transactions.length>0" class="w-full flex flex-column surface-0
            align-items-left overflow-hidden
            border-round-xl xl:border-round-2xl lg:border-round-2xl md:border-round-xl">

            <div v-for="t of transactions" @click="showTxPopup(t)" 
                class="w-full flex flex-column px-3 py-3 border-bottom-1 border-100 cursor-pointer hover:bg-surface-50 transition-colors">
                <div class="w-full flex flex-row gap-2">
                    <div class="flex flex-1 flex-row gap-2 align-items-center">
                        <div class="flex">
                            <Avatar v-if="t.type === 'transfer'" icon="pi pi-arrow-right-arrow-left" class="mr-2 text-600" size="small" shape="circle" />
                            <Avatar v-else-if="t.type === 'withdraw'" icon="pi pi-arrow-up" class="mr-2 text-600" size="small" shape="circle" />
                            <Avatar v-else-if="t.type === 'topup'" icon="pi pi-arrow-down" class="mr-2 text-600" size="small" shape="circle" />
                            <Avatar v-else-if="t.type === 'deposit'" icon="pi pi-plus" class="mr-2 text-600" size="small" shape="circle" />
                            <Avatar v-else icon="pi pi-file" class="mr-2 text-600" size="small" shape="circle" />
                        </div>
                        <div class="flex flex-column gap-1">
                            <span class="flex text-900 text-sm text-left long-word">
                                {{ getShort(t.description) }}
                            </span>
                            <div class="flex text-xs text-left gap-2">
                                <span class="flex text-500">
                                    {{ getFormatDate(t.created_at )}}
                                </span>
                                <span class="flex text-500 uppercase" :class="getClassOfStatus(t.status)">
                                    {{ t.status }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="flex flex-column gap-1 text-right">
                        <span class="flex text-900 text-sm font-medium text-right">
                            {{ get_format_amount(t.amount, t.currency) }}
                        </span>
                        <span v-if="t.amount_usd" class="flex text-600 text-xs text-right">
                            {{ get_format_amount(t.amount_usd, 'usd') }}
                        </span>
                    </div>
                </div>
            </div>
            <Paginator v-if="total && total>limit" :rows="limit" :first="skip" :totalRecords="total" :rowsPerPageOptions="[10, 20, 30]" @update:first="onChangePage" 
                template="PrevPageLink PageLinks NextPageLink" />
        </div>
        <div v-else class="w-full flex flex-column p-3 surface-0 border-round-xl xl:border-round-2xl lg:border-round-2xl md:border-round-xl">
            <span class="text-900 text-sm text-center">
                {{ content[lang_].empty }}
            </span>
        </div>
    </div>
    <Skeleton v-if="loading" height="2rem" class="mb-3" />

    <Dialog class="w-full border-none" modal @hide="currentTx = null"
        v-model:visible="popupTx" 
        :position="screenSize=='xs' ? 'bottom' : 'center'"
        :style="(screenSize=='xs' || screenSize=='sm' || screenSize=='md') ? {width: '100%!important', height: '100%!important', margin: '0!important', padding: '0!important'} : { width: 'auto' }" 
        :contentStyle="(screenSize=='xs' || screenSize=='sm' || screenSize=='md') ? { height: '100%' } : { height: 'auto' }"
        :class="(screenSize=='xs') ? 'h-full border-noround-bottom' : 'max-w-30rem'">

        <template #header>
            <span class="text-lg font-medium">{{ content[lang_].detailTile }}</span>
        </template>
     
        <div v-if="currentTx" class="flex flex-column gap-3 align-items-left max-w-30rem"> 
            <div v-if="currentTx.id" class="flex flex-row gap-1 align-items-center">
                <span class="flex flex-1">{{ content[lang_].detailId }}:</span>
                <span class="flex">{{ currentTx.id }}</span>
            </div>
            <div v-if="currentTx.merchant_name" class="flex flex-row gap-1 align-items-center">
                <span class="flex flex-1">{{ content[lang_].detailMerchant }}:</span>
                <span class="flex">{{ currentTx.merchant_name }}</span>
            </div>
            <div v-if="currentTx.merchant_category_code" class="flex flex-row gap-1 align-items-center">
                <span class="flex flex-1">{{ content[lang_].detailCategory }}:</span>
                <span class="flex">{{ currentTx.merchant_category_code }}</span>
            </div>
            <div v-if="currentTx.merchant_country" class="flex flex-row gap-1 align-items-center">
                <span class="flex flex-1">{{ content[lang_].detailCountry }}:</span>
                <span class="flex">{{ currentTx.merchant_country }}</span>
            </div>
            <div v-if="currentTx.description" class="flex flex-column gap-1 align-items-left">
                <span class="flex flex-1">{{ content[lang_].detailDescription }}:</span>
                <Message severity="secondary" :text="currentTx.description" class="w-full">
                    <span class="font-normal text-sm">{{ currentTx.description }}</span>
                </Message>
            </div>
            <div v-if="currentTx.amount_usd" class="flex flex-row gap-1 align-items-center">
                <span class="flex flex-1">{{ content[lang_].detailAmountUSD }}:</span>
                <span class="flex">{{ currentTx.amount_usd }}</span>
            </div>
            <div v-if="currentTx.amount" class="flex flex-row gap-1 align-items-center">
                <span class="flex flex-1">{{ content[lang_].detailAmount }}:</span>
                <span class="flex">{{ currentTx.amount }}</span>
            </div>
            <div v-if="currentTx.currency" class="flex flex-row gap-1 align-items-center">
                <span class="flex flex-1">{{ content[lang_].detailCurrency }}:</span>
                <span class="flex uppercase">{{ currentTx.currency }}</span>
            </div>
            <div v-if="currentTx.amount_usd" class="flex flex-row gap-1 align-items-center">
                <span class="flex flex-1">{{ content[lang_].detailAmountUSD }}:</span>
                <span class="flex">{{ currentTx.amount_usd }}</span>
            </div>
            <div v-if="currentTx.status" class="flex flex-row gap-1 align-items-center">
                <span class="flex flex-1">{{ content[lang_].detailStatus }}:</span>
                <span class="flex uppercase" :class="getClassOfStatus(currentTx.status)">{{ currentTx.status }}</span>
            </div>
            <div v-if="currentTx.failure_reason" class="flex flex-row gap-1 align-items-center">
                <span class="flex flex-1">{{ content[lang_].detailFailureReason }}:</span>
                <span class="flex">{{ currentTx.failure_reason }}</span>
            </div>
            <div v-if="currentTx.is_reversal" class="flex flex-row gap-1 align-items-center">
                <span class="flex flex-1">{{ content[lang_].detailReversal }}:</span>
                <span class="flex">{{ currentTx.is_reversal }}</span>
            </div>
            <div v-if="currentTx.created_at" class="flex flex-row gap-1 align-items-center">
                <span class="flex flex-1">{{ content[lang_].detailCreatedAt }}:</span>
                <span class="flex">{{ currentTx.created_at }}</span>
            </div>
            <div v-if="currentTx.hash" class="flex flex-row gap-1 align-items-center">
                <span class="flex flex-1">{{ content[lang_].detailHash }}:</span>
                <span class="flex">{{ currentTx.hash }}</span>
            </div>
        </div>
       
        <template #footer>
            <Button :label="content[lang_].detailBtnClose" icon="pi pi-times" @click="popupTx = false" class="w-full" size="large" severity="secondary" />
        </template>
    </Dialog>
</template>

<script>
import Connect from         '@/assets/js/Connect'
import Helper from          '@/assets/js/Helper'

export default {
    props: {
        transactions: { 
            type: Array, 
            default: () => [] 
        },
        skip: { 
            type: Number, 
            default: 10
        },
        limit: { 
            type: Number, 
            default: 10 
        },
        total: { 
            type: Number, 
            default: 0 
        },
        loading: { 
            type: Boolean, 
            default: true 
        }
    },
    data() {
        return {
            content: {
                ru: { 
                    empty: 'Нет транзакций',
                    trans: 'транзакций',
                    from: 'из',
                    detailTile: 'Детали транзакции',
                    detailId: 'ID транзакции',
                    detailMerchant: 'Мерчант',
                    detailCategory: 'Категория мерчанта',
                    detailCountry: 'Страна мерчанта',
                    detailDescription: 'Описание',
                    detailAmount: 'Сумма',
                    detailAmountUSD: 'Сумма (USD)',
                    detailCurrency: 'Валюта',
                    detailStatus: 'Статус',
                    detailFailureReason: 'Причина сбоя',
                    detailReversal: 'Возврат',
                    detailCreatedAt: 'Дата создания',
                    detailBtnClose: 'Закрыть',
                    detailHash: 'Хэш',
                },
                en: { 
                    empty: 'No transactions',
                    trans: 'transactions',
                    from: 'from',
                    detailTile: 'Transaction Details',
                    detailId: 'Transaction ID',
                    detailMerchant: 'Merchant',
                    detailCategory: 'Merchant Category',
                    detailCountry: 'Merchant Country',
                    detailDescription: 'Description',
                    detailAmount: 'Amount',
                    detailAmountUSD: 'Amount (USD)',
                    detailCurrency: 'Currency',
                    detailStatus: 'Status',
                    detailFailureReason: 'Failure Reason',
                    detailReversal: 'Reversal',
                    detailCreatedAt: 'Created At',
                    detailBtnClose: 'Close',
                    detailHash: 'Hash',
                },
            },
            count: 0,
            popupTx: false,
            currentTx: null,
        }
    },
    computed: {
        lang_() { return (this.$i18n.locale) ? this.$i18n.locale : 'ru' },
        screenSize() { return this.$store.getters.StateScreenSize },
    },
    watch: {
        transactions: {
            handler: function (val) {
                if(val){
                    this.count = val.length + this.skip
                    if(this.count > this.total) this.count = this.total
                }
            },
            deep: true
        }
    },
    methods: {
        get_format_amount(value, currency) {
            return this.helper.format_amount(value, currency, 1)
        },
        getShort(value){
            if(!value) return ''
            return this.helper.get_short_name(value, 70)
        },
        getFormatDate(date) {
            return this.helper.format_date_short(date)
        },
        onChangePage(event) {
            this.$emit('update:first', event)
            window.scrollTo({ top: 0, behavior: 'smooth' }) //скролл вверх
        },
        onChangeRows(event) {
            this.$emit('update:rows', event)
            window.scrollTo({ top: 0, behavior: 'smooth' }) //скролл вверх
        },
        showTxPopup(tx) {
            this.currentTx = tx
            this.popupTx = true
        },
        getClassOfStatus(status) {
            if(!status) return 'text-gray-600';
            let UPPERCASE = (status) ? status.toUpperCase() : ''
            if (UPPERCASE === 'COMPLETED' || UPPERCASE === 'APPROVED') return 'text-green-500';
            if (UPPERCASE === 'DECLINED') return 'text-red-500';
            if (UPPERCASE === 'PENDING') return 'text-yellow-600';
            return 'text-primary';
        }
    },
    async beforeCreate(){
        this.connect = new Connect()
        this.helper = new Helper()
    },
}
</script>